import { commonUtils } from '@sato/components';
import { AxiosResponse } from 'axios';
import { differenceInSeconds } from 'date-fns';
import { pick } from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';

import { api } from '@api';
import { defaultAvailableLanguage } from '@constants';
import { GA_EVENT_TYPES } from '@models/analyticsEvents.types';
import { User, UserResponse } from '@models/user.types';
import { sendGoogleAnalyticsEvent } from '@utils/analytics.utils';
import { submitGiosgVisitor } from '@utils/giosg.utils';

import { createOnAuthenticateListener } from './authStore/authStore.utils';
import { RequestQueue } from './utils/RequestQueue';
import { taskStore } from './taskStore';

const USER_REQUEST_ID = 'fetch-user-data';
const TOKEN_EXPIRE = 3600; // seconds, 1 hour

export class UserStore {
  user: User | null = null;
  myInformationFetchDate: number | null = null;

  private requestQueue = new RequestQueue();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    createOnAuthenticateListener(() => {
      this.fetchUser();
    }, this.clear);
  }

  clear() {
    this.user = null;
    this.myInformationFetchDate = null;
  }

  get isFetching() {
    return this.requestQueue.isFetching;
  }

  get address() {
    return this.user?.address;
  }

  get isLoaded() {
    return !!this.myInformationFetchDate;
  }

  get infoLanguage() {
    return this.user?.infoMaterialLanguage ?? defaultAvailableLanguage.key;
  }

  get communicationsLanguage() {
    return this.user?.communicationsLanguage ?? defaultAvailableLanguage.key;
  }

  async fetchUser() {
    this.requestQueue.fetch(USER_REQUEST_ID, async () => {
      let requestPromise: Promise<AxiosResponse<UserResponse>>;

      // show global loading indicator only if user data in not initially defined
      if (!this.user) {
        requestPromise = taskStore.addTaskAndRun({
          id: USER_REQUEST_ID,
          action: api.user.getUser,
        });
      } else {
        requestPromise = api.user.getUser();
      }

      const { data, status } = await requestPromise;

      runInAction(() => {
        if (status !== 200) return;

        this.user = data.user;
        this.myInformationFetchDate = Date.now();

        submitGiosgVisitor(data.user);
      });
    });
  }

  async updateUser(data: Partial<User>) {
    if (this.isFetching) {
      // TODO: should we display a loader
      return console.warn(
        'Updating user data, cannot make changes at the moment'
      );
    }

    // fetch user data if user hasn't been initialized yet
    if (!this.user) await this.fetchUser();

    // change UI already before submitting changes
    const changedState = pick(this.user, Object.keys(data));
    this.user = { ...(this.user as User), ...data };

    this.requestQueue.fetch(
      USER_REQUEST_ID,
      async () => {
        const { status } = await api.user.update(data);

        if (status !== 200) throw Error('Updating user failed');

        sendGoogleAnalyticsEvent({ event: GA_EVENT_TYPES.EDIT_MY_INFO });
      },
      {
        onError: () => {
          // revert changes on update error
          runInAction(() => {
            this.user = { ...(this.user as User), ...changedState };
          });
        },
      }
    );
  }

  refreshUser() {
    // logout if token has expired
    if (
      !commonUtils.nonNullable(this.myInformationFetchDate) ||
      differenceInSeconds(Date.now(), this.myInformationFetchDate) >
        TOKEN_EXPIRE ||
      !commonUtils.nonNullable(this.user)
    ) {
      // TODO: finalize logout logic and redirect to current page if needed
      this.clear();
    } else {
      this.fetchUser();
    }
  }
}

export const userStore = new UserStore();
